import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, GraduationCap, BookMarked, BookA, BookOpen, CornerDownLeft } from "lucide-react";
import { searchAll } from "@/lib/searchIndex";

const ICON = { module: GraduationCap, code: BookMarked, term: BookA, guide: BookOpen };

// Site-wide search (navbar): modules, NFPA 72 code sections, glossary terms and guides, in the active language.
export default function SiteSearch({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const results = useMemo(() => searchAll(i18n.language, q), [i18n.language, q]);

  useEffect(() => { if (open) { setQ(""); setCursor(0); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => { setCursor(0); }, [q]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;

  const go = (item) => { onClose(); navigate(item.to); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter" && results[cursor]) go(results[cursor]);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-start justify-center p-3 sm:pt-[12vh]" role="dialog" aria-modal="true" aria-label={t("search.title")} data-testid="site-search" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0B132B] shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-3 border-b border-white/10">
          <Search size={18} className="text-slate-400 shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} data-testid="site-search-input" placeholder={t("search.placeholder")}
            className="flex-1 h-14 bg-transparent text-base text-white placeholder:text-slate-500 outline-none min-w-0" autoComplete="off" />
          <button type="button" onClick={onClose} aria-label={t("common.close", "Close")} data-testid="site-search-close" className="grid place-items-center w-10 h-10 rounded-md text-slate-400 hover:text-white hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="site-search-results">
          {q.trim().length < 2 ? (
            <p className="px-4 py-6 text-sm text-slate-500">{t("search.hint")}</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-400" data-testid="site-search-empty">{t("search.empty", { q })}</p>
          ) : (
            <ul className="py-2">
              {results.map((r, i) => {
                const Icon = ICON[r.type];
                return (
                  <li key={`${r.type}-${r.id}`}>
                    <button type="button" onClick={() => go(r)} onMouseEnter={() => setCursor(i)} data-testid={`site-search-result-${r.type}-${r.id}`} aria-selected={i === cursor}
                      className={`w-full text-left flex items-start gap-3 px-4 py-2.5 min-h-12 ${i === cursor ? "bg-primary/15" : "hover:bg-white/5"}`}>
                      <span className="grid place-items-center w-8 h-8 rounded-md bg-secondary/60 text-primary shrink-0 mt-0.5"><Icon size={15} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-white font-semibold leading-snug truncate">{r.title}</span>
                        <span className="block text-xs text-slate-400 truncate">{t(`search.types.${r.type}`)}{r.hint ? ` · ${r.hint}` : ""}</span>
                      </span>
                      {i === cursor && <CornerDownLeft size={14} className="text-slate-500 shrink-0 mt-2 hidden sm:block" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
